import { useEffect, useState } from 'react';
import { App, Button, Card, Space, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { scopes } from '../../../../authConfig';
import { getNoeticaAutodialConfig, putNoeticaAutodialConfig } from '../../../../services/admin-page-service';
import type { NoeticaAutodialAdminConfig } from '../../../../dto/admin-config-models';

const { Title, Text } = Typography;

const linesToText = (lines: string[] | undefined | null) =>
  (Array.isArray(lines) ? lines : []).map((s) => (s ?? '').trim()).filter((s) => s.length > 0).join('\n');

const textToLines = (text: string) =>
  text
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

/** E.164-style: optional leading +, then digits only (no spaces, parentheses, or dashes). */
const phoneLinePattern = /^\+?[0-9]+$/;

const findInvalidCliLine = (lines: string[]): { line: number; value: string } | null => {
  for (let i = 0; i < lines.length; i++) {
    if (!phoneLinePattern.test(lines[i])) {
      return { line: i + 1, value: lines[i] };
    }
  }
  return null;
};

/** Blocked list: digits, +, -, (, ) only (no spaces or letters). */
const blockedLinePattern = /^[0-9+()\-]+$/;

const findInvalidBlockedLine = (lines: string[]): { line: number; value: string } | null => {
  for (let i = 0; i < lines.length; i++) {
    if (!blockedLinePattern.test(lines[i])) {
      return { line: i + 1, value: lines[i] };
    }
  }
  return null;
};

const AdminAutodialConfig = () => {
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState<Pick<NoeticaAutodialAdminConfig, 'lastModifiedUtc' | 'modifiedBy'> | null>(null);
  const [cliText, setCliText] = useState('');
  const [blockedText, setBlockedText] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const data = await getNoeticaAutodialConfig(token.accessToken);
        if (data) {
          const cliJoined = linesToText(data.cliRotationNumbers);
          const blockedJoined = linesToText(data.blockedPhoneNumbers);
          setCliText(cliJoined);
          setBlockedText(blockedJoined);
          setMeta({ lastModifiedUtc: data.lastModifiedUtc, modifiedBy: data.modifiedBy });
          if (cliJoined.length === 0) {
            setLoadError(
              'CLI list is empty — check the NoeticaAutodialAdminConfig row and that migration 122 has been applied.',
            );
          }
        } else {
          setLoadError('Could not load config. Check administrator permissions and the API response.');
          notification.error({ message: 'Failed to load autodial configuration' });
        }
      } catch (e) {
        setLoadError(String(e));
        notification.error({ message: 'Failed to load autodial config', description: String(e) });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [instance, inProgress, notification]);

  const save = async () => {
    const cliRotationNumbers = textToLines(cliText);
    const blockedPhoneNumbers = textToLines(blockedText);

    const invalidCli = findInvalidCliLine(cliRotationNumbers);
    if (invalidCli) {
      notification.error({
        message: 'Invalid CLI number',
        description: `Line ${invalidCli.line}: only digits and an optional leading + are allowed (example: +4917012345678). Got: "${invalidCli.value}"`,
      });
      return;
    }

    const invalidBlocked = findInvalidBlockedLine(blockedPhoneNumbers);
    if (invalidBlocked) {
      notification.error({
        message: 'Invalid blocked number',
        description: `Line ${invalidBlocked.line}: only digits, +, -, (, and ) are allowed (example: +49(170)1234567). Got: "${invalidBlocked.value}"`,
      });
      return;
    }

    try {
      setSaving(true);
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const ok = await putNoeticaAutodialConfig(token.accessToken, { cliRotationNumbers, blockedPhoneNumbers });
      if (!ok) {
        notification.error({ message: 'Save failed', description: 'Server returned an error.' });
        return;
      }
      notification.success({ message: 'Autodial config saved' });
      const data = await getNoeticaAutodialConfig(token.accessToken);
      if (data) {
        setCliText(linesToText(data.cliRotationNumbers));
        setBlockedText(linesToText(data.blockedPhoneNumbers));
        setMeta({ lastModifiedUtc: data.lastModifiedUtc, modifiedBy: data.modifiedBy });
      }
    } catch (e) {
      notification.error({ message: 'Save failed', description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const cliLines = textToLines(cliText);
  const blockedLines = textToLines(blockedText);

  return (
    <div style={{ padding: 24, flex: 1 }}>
      <Title level={4}>Autodialer config (Noetica)</Title>
      <Text type="secondary">
        Values below are <strong>loaded from the database</strong>. CLI numbers rotate in order for each opportunity sent
        to Noetica — each CLI line must be digits only with an optional leading <code>+</code>. Each blocked line may
        contain only digits, <code>+</code> (including several), <code>-</code>, and parentheses; matching still uses{' '}
        <strong>digits only</strong> after normalization, plus regex masks and the five-or-more identical digits rule.
      </Text>
      {loadError && (
        <div style={{ marginTop: 12 }}>
          <Text type="danger">{loadError}</Text>
        </div>
      )}
      {meta && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">
            Last updated: {new Date(meta.lastModifiedUtc).toLocaleString()} {meta.modifiedBy ? `— ${meta.modifiedBy}` : ''}
          </Text>
        </div>
      )}
      {!loading && !loadError && (
        <div style={{ marginTop: 8 }}>
          <Text>
            Loaded lists: <strong>{cliLines.length}</strong> CLI number(s), <strong>{blockedLines.length}</strong> blocked
            number(s). Edit the lines below and click Save.
          </Text>
        </div>
      )}
      <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 16 }}>
        <Card
          title="CLI rotation (one number per line)"
          loading={loading}
          extra={<Text type="secondary">{cliLines.length} entries</Text>}>
          <TextArea
            rows={12}
            value={cliText}
            onChange={(e) => setCliText(e.target.value)}
            placeholder="Numbers from the database appear here after load…"
            style={{ fontFamily: 'monospace' }}
          />
        </Card>
        <Card
          title="Blocked phone numbers for Noetica (one per line)"
          loading={loading}
          extra={<Text type="secondary">{blockedLines.length} entries</Text>}>
          <TextArea
            rows={18}
            value={blockedText}
            onChange={(e) => setBlockedText(e.target.value)}
            placeholder="Numbers from the database appear here after load…"
            style={{ fontFamily: 'monospace' }}
          />
        </Card>
        <Button type="primary" onClick={save} loading={saving} disabled={loading}>
          Save
        </Button>
      </Space>
    </div>
  );
};

export default AdminAutodialConfig;
